import React, { useState, useEffect } from 'react';
import './NodeTable.css';
import Pagination from './NodePagination';
import loader from "../../assets/loader.gif";

const NodeTable = ({ searchTerm, selectedClusters, selectedEnvironment, environmentData, loading, error }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });

  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    console.log("environment data node monitor: ", environmentData);
    console.log("selected clusters in NodeTable: ", selectedClusters);
    processEnvironmentData();
  }, [environmentData, selectedClusters, searchTerm, sortConfig]); // Added sortConfig dependency

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedClusters, environmentData]);

  // Convert CPU and memory values to standardized formats
  const normalizeResourceValue = (value) => {
    if (!value || value === 'N/A') return { value: 'N/A', rawValue: 0 };

    // Handle CPU values
    if (value.endsWith('m')) {
      return {
        value: value,
        rawValue: parseInt(value.slice(0, -1)) / 1000
      };
    } else if (value.endsWith('n')) {
      return {
        value: `${(parseInt(value.slice(0, -1)) / 1000000).toFixed(0)}m`,
        rawValue: parseInt(value.slice(0, -1)) / 1000000
      };
    }

    // Handle numeric CPU values (convert to millicores format)
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && !value.includes("B") && !value.includes("i")) {
      return {
        value: `${Math.round(numValue * 1000)}m`,
        rawValue: numValue
      };
    }

    // Handle memory values
    if (value.endsWith('Ki')) {
      return {
        value: `${Math.round(parseInt(value.slice(0, -2)) / 1024)} MB`,
        rawValue: parseInt(value.slice(0, -2)) / 1024
      };
    } else if (value.endsWith('Mi')) {
      return {
        value: `${parseInt(value.slice(0, -2))} MB`,
        rawValue: parseInt(value.slice(0, -2))
      };
    } else if (value.endsWith('Gi')) {
      return {
        value: `${parseInt(value.slice(0, -2))} GB`,
        rawValue: parseInt(value.slice(0, -2)) * 1024
      };
    }

    // Return as is if no unit matches
    return { value: value, rawValue: parseFloat(value) || 0 };
  };

  // Calculate percentage
  const calculatePercentage = (usage, limit) => {
    if (usage.rawValue === 0 || limit.rawValue === 0) return 'N/A';
    return ((usage.rawValue / limit.rawValue) * 100).toFixed(1);
  };

  const calculatePercentageCpu = (usage, limit) => {
    if (usage.rawValue === 0 || limit.rawValue === 0) return 'N/A';
    return ((usage.rawValue / limit.rawValue) / 10).toFixed(1);
  };

  // Process environment data to extract node information
  const processEnvironmentData = () => {
    if (!environmentData || !Array.isArray(environmentData) || environmentData.length === 0) {
      setTableData([]);
      return;
    }

    const processedData = [];

    // Process each node in the data
    environmentData.forEach(node => {
      // Process node data
      const clusterName = node.cluster;
      const nodeName = node.name;

      // Process CPU data
      const cpuLimit = normalizeResourceValue(node.allocatable?.cpu || 'N/A');
      const cpuUsage = normalizeResourceValue(node.usage?.cpu || 'N/A');
      const cpuPercent = calculatePercentageCpu(cpuUsage, cpuLimit);

      // Process memory data
      const memoryLimit = normalizeResourceValue(node.allocatable?.memory || 'N/A');
      const memoryUsage = normalizeResourceValue(node.usage?.memory || 'N/A');
      const memoryPercent = calculatePercentage(memoryUsage, memoryLimit);

      // Add to processed data with raw values for sorting
      processedData.push({
        clusterName,
        nodeName,
        cpuUsage: cpuUsage.value,
        cpuLimit: cpuLimit.value,
        cpuPercent,
        memoryUsage: memoryUsage.value,
        memoryLimit: memoryLimit.value,
        memoryPercent,
        // Store raw values for sorting
        rawValues: {
          cpuUsage: cpuUsage.rawValue,
          cpuLimit: cpuLimit.rawValue,
          memoryUsage: memoryUsage.rawValue,
          memoryLimit: memoryLimit.rawValue
        }
      });
    });

    // Apply search filter if any
    let filteredData = processedData;
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filteredData = filteredData.filter(item => {
        // Check cluster name and node name
        if ((item.clusterName && item.clusterName.toLowerCase().includes(term)) ||
          (item.nodeName && item.nodeName.toLowerCase().includes(term))) {
          return true;
        }

        // Check numeric values - convert them to string for searching
        if ((item.cpuUsage && item.cpuUsage.toString().includes(term)) ||
          (item.cpuLimit && item.cpuLimit.toString().includes(term)) ||
          (item.cpuPercent && item.cpuPercent.toString().includes(term)) ||
          (item.memoryUsage && item.memoryUsage.toString().includes(term)) ||
          (item.memoryLimit && item.memoryLimit.toString().includes(term)) ||
          (item.memoryPercent && item.memoryPercent.toString().includes(term))) {
          return true;
        }

        return false;
      });
    }

    // Apply sorting if configured
    if (sortConfig.key) {
      console.log("Sorting by:", sortConfig.key, "direction:", sortConfig.direction);
      
      filteredData.sort((a, b) => {
        // Based on the key, determine how to sort
        let comparison = 0;
        
        if (sortConfig.key === 'clusterName' || sortConfig.key === 'nodeName') {
          // Sort alphabetically for cluster and node names
          const aValue = a[sortConfig.key] || '';
          const bValue = b[sortConfig.key] || '';
          comparison = aValue.localeCompare(bValue);
        } 
        else if (sortConfig.key === 'cpuPercent' || sortConfig.key === 'memoryPercent') {
          // Handle percentage values
          const aValue = a[sortConfig.key] === 'N/A' ? -1 : parseFloat(a[sortConfig.key]);
          const bValue = b[sortConfig.key] === 'N/A' ? -1 : parseFloat(b[sortConfig.key]);
          comparison = aValue - bValue;
        }
        else if (sortConfig.key === 'cpuUsage' || sortConfig.key === 'cpuLimit') {
          // Use raw values for CPU sorting
          const cpuKey = sortConfig.key === 'cpuUsage' ? 'cpuUsage' : 'cpuLimit';
          const aValue = a.rawValues[cpuKey];
          const bValue = b.rawValues[cpuKey];
          comparison = aValue - bValue;
        }
        else if (sortConfig.key === 'memoryUsage' || sortConfig.key === 'memoryLimit') {
          // Use raw values for memory sorting
          const memKey = sortConfig.key === 'memoryUsage' ? 'memoryUsage' : 'memoryLimit';
          const aValue = a.rawValues[memKey];
          const bValue = b.rawValues[memKey];
          comparison = aValue - bValue;
        }
        
        // Reverse the comparison if direction is descending
        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }

    setTableData(filteredData);
  };

  const handleSort = (key) => {
    console.log("Sort button clicked for key:", key);
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    setSortConfig({ key, direction });
  };

  // Highlight text function to highlight search term in string
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const term = searchTerm.toLowerCase().trim();
    if (term === '') return text;

    const textStr = String(text);
    const textLower = textStr.toLowerCase();

    if (!textLower.includes(term)) return text;

    const parts = [];
    let lastIndex = 0;
    let index = textLower.indexOf(term);

    while (index !== -1) {
      // Add the text before the match
      if (index > lastIndex) {
        parts.push(textStr.substring(lastIndex, index));
      }

      // Add the highlighted match
      parts.push(
        <span key={`highlight-${index}`} style={{ backgroundColor: 'rgb(25, 82, 25)', color: 'white', fontWeight: 'bold' }}>
          {textStr.substring(index, index + term.length)}
        </span>
      );

      lastIndex = index + term.length;
      index = textLower.indexOf(term, lastIndex);
    }

    // Add any text after the last match
    if (lastIndex < textStr.length) {
      parts.push(textStr.substring(lastIndex));
    }

    return parts;
  };

  // Updated SortButtons component with direct onClick handlers
  const SortButtons = ({ sortKey, label }) => {
    const isAscActive = sortConfig.key === sortKey && sortConfig.direction === 'ascending';
    const isDescActive = sortConfig.key === sortKey && sortConfig.direction === 'descending';
    
    return (
      <div className="header-sort-wrapper">
        <span className="header-label">{label}</span>
        <div className="sort-button-container">
          <button
            type="button"
            className={`sort-button up ${isAscActive ? 'active' : ''}`}
            onClick={() => {
              console.log(`Ascending sort clicked for ${sortKey}`);
              setSortConfig({ key: sortKey, direction: 'ascending' });
            }}
          >
            ▲
          </button>
          <button
            type="button"
            className={`sort-button down ${isDescActive ? 'active' : ''}`}
            onClick={() => {
              console.log(`Descending sort clicked for ${sortKey}`);
              setSortConfig({ key: sortKey, direction: 'descending' });
            }}
          >
            ▼
          </button>
        </div>
      </div>
    );
  };

  // Calculate pagination
  const totalItems = tableData.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (itemsCount) => {
    setItemsPerPage(itemsCount);
    setCurrentPage(1);
  };

  return (
    <div className="node-table-container">
      <>
        <table className="node-table">
          <thead className='node-table-thead'>
            <tr>
              <th rowSpan="2" className="cluster-name-col">
                <SortButtons sortKey="clusterName" label="Cluster Name" />
              </th>
              <th rowSpan="2" className="node-name-col">
                <SortButtons sortKey="nodeName" label="Node Name" />
              </th>
              <th colSpan="2" className="cpu-header">CPU</th>
              <th colSpan="2" className="memory-header">Memory</th>
            </tr>

            <tr>
              <th className="cpu-usage-col"> <SortButtons sortKey="cpuUsage" label="Usage" /> </th>
              {/* <th className="cpu-limit-col"> <SortButtons sortKey="cpuLimit" label="Limit" /> </th> */}
              <th className="cpu-percent-col"> <SortButtons sortKey="cpuPercent" label="%" /> </th>
              <th className="memory-usage-col"> <SortButtons sortKey="memoryUsage" label="Usage" /> </th>
              {/* <th className="memory-limit-col"> <SortButtons sortKey="memoryLimit" label="Limit" /> </th> */}
              <th className="memory-percent-col"> <SortButtons sortKey="memoryPercent" label="%" /> </th>
            </tr>
          </thead>
          <tbody className='node-table-tbody'>
            {loading && (
              <tr>
                <td colSpan={8} className="status-message">
                  <div className="loading">
                    <img src={loader} alt="Loading..." />
                  </div>
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={8} className="status-message-error">Error: {error}</td>
              </tr>
            )}
            {!loading && !error && currentItems.length === 0 && (
              <tr>
                <td colSpan={8} className="status-message">No data found</td>
              </tr>
            )}
            {!loading && !error && currentItems.map((row, index) => {
              return (
                <tr key={index}>
                  <td className="cluster-name-col">{highlightText(row.clusterName, searchTerm)}</td>
                  <td className="node-name-col" title={row.nodeName}>{highlightText(row.nodeName, searchTerm)}</td>
                  <td className="cpu-usage-col">{highlightText(row.cpuUsage, searchTerm)}</td>
                  {/* <td className="cpu-limit-col">{highlightText(row.cpuLimit, searchTerm)}</td> */}
                  <td className="cpu-percent-col" style={{ backgroundColor: row.cpuPercent > 80 ? '#E78587' : 'inherit', color: row.cpuPercent > 80 ? 'white' : 'inherit' }}>
                    {row.cpuPercent === 'N/A' ? 'N/A' : highlightText(`${row.cpuPercent}%`, searchTerm)}
                  </td>
                  <td className="memory-usage-col">{highlightText(row.memoryUsage, searchTerm)}</td>
                  {/* <td className="memory-limit-col">{highlightText(row.memoryLimit, searchTerm)}</td> */}
                  <td className="memory-percent-col" style={{ backgroundColor: row.memoryPercent > 80 ? '#E78587' : 'inherit', color: row.memoryPercent > 80 ? 'white' : 'inherit' }}>
                    {row.memoryPercent === 'N/A' ? 'N/A' : highlightText(`${row.memoryPercent}%`, searchTerm)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </>
    </div>
  );
};

export default NodeTable;